import { ApolloClient, InMemoryCache } from '@apollo/client-integration-nextjs';
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
} from '@apollo/client/errors';
import { ApolloLink } from '@apollo/client/link';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';

import { BackendGraphqlApiConfig } from './get-backend-graphql-config';

import { getLogger } from '@/logger/logger';

export const makeApolloClient = (apiConfig: BackendGraphqlApiConfig) => {
  const uri = new URL(apiConfig.baseUrl);

  const { fetchOptions } = apiConfig;

  const logger = getLogger('ApolloClient');

  logger.debug('Creating Apollo Client');

  const httpLink = new HttpLink({
    uri: uri.toString(),
  });

  const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path }) =>
        logger.error('GraphQL Error', {
          graphql: { event: 'graphql_error', reason: message, locations, path },
        }));

      return;
    }

    if (CombinedProtocolErrors.is(error)) {
      error.errors.forEach(({ message, extensions }) =>
        logger.error('Protocol Error', {
          graphql: { event: 'protocol_error', reason: message, extensions: JSON.stringify(extensions) },
        }));

      return;
    }

    logger.error('Unhandled Apollo error', { error });
  });

  return new ApolloClient({
    link: ApolloLink.from([
      errorLink,
      httpLink,
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
    defaultContext: {
      fetchOptions,
    },
  });
};
